import { createSlice } from '@reduxjs/toolkit';
import {
  fetchStockSvgThunk,
  createStockSvgThunk,
  deleteStockSvgThunk
} from './iconStocksActionThunks';

const initialState = {
  stockIcons: {}, // Stores stock icons with their symbols as keys: { symbol: { svg, id } }
  // Symbols with a fetch currently in flight -- lets fetchStockSvgThunk's
  // `condition` bail out synchronously when many <StockLogo> instances for
  // the same symbol (e.g. a ticker appearing in both "Top Gainers" and the
  // main grid) mount in the same tick, before any of their requests have
  // resolved. Without this, a page with hundreds of cards fired one real
  // HTTP request per card-instance -- 1810 requests for a single
  // /stocks-overview load, enough to trip the browser's own connection-pool
  // limit (net::ERR_INSUFFICIENT_RESOURCES) -- found by the pass-28 route sweep.
  pendingSymbols: [],
  loading: false,
  error: null,
};

const stockIconsSlice = createSlice({
  name: 'stockIcons',
  initialState,
  reducers: {
    resetError: (state) => {
      state.error = null;
    },
    clearStockIcons: (state) => {
      state.stockIcons = {};
    },
  },
  extraReducers: (builder) => {
    // Fetch Stock SVG by Symbol
    builder
      .addCase(fetchStockSvgThunk.pending, (state, { meta }) => {
        state.loading = true;
        state.error = null;
        const symbol = meta.arg?.toUpperCase?.().trim();
        if (symbol && !state.pendingSymbols.includes(symbol)) {
          state.pendingSymbols.push(symbol);
        }
      })
      .addCase(fetchStockSvgThunk.fulfilled, (state, { payload, meta }) => {
        // payload.data should contain the API response with { symbol, svg, id }
        const { symbol, data } = payload;
        if (data && data.svg) {
          state.stockIcons[symbol] = {
            svg: data.svg,
            id: data.id,
            symbol: data.symbol
          };
        }
        state.loading = false;
        const reqSymbol = meta.arg?.toUpperCase?.().trim();
        state.pendingSymbols = state.pendingSymbols.filter((s) => s !== reqSymbol);
      })
      .addCase(fetchStockSvgThunk.rejected, (state, { payload, meta }) => {
        state.loading = false;
        state.error = payload || 'Failed to fetch stock SVG';
        const reqSymbol = meta.arg?.toUpperCase?.().trim();
        state.pendingSymbols = state.pendingSymbols.filter((s) => s !== reqSymbol);
      });

    // Create Stock SVG
    builder
      .addCase(createStockSvgThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createStockSvgThunk.fulfilled, (state, { payload }) => {
        // payload should contain the created stock SVG data
        if (payload && payload.svg && payload.symbol) {
          state.stockIcons[payload.symbol] = {
            svg: payload.svg,
            id: payload.id,
            symbol: payload.symbol
          };
        }
        state.loading = false;
      })
      .addCase(createStockSvgThunk.rejected, (state, { payload }) => {
        state.loading = false;
        state.error = payload || 'Failed to create stock SVG';
      });

    // Delete Stock SVG
    builder
      .addCase(deleteStockSvgThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteStockSvgThunk.fulfilled, (state, { payload }) => {
        // Find and remove the stock icon by ID
        const symbolToDelete = Object.keys(state.stockIcons).find(
          symbol => state.stockIcons[symbol].id === payload
        );
        if (symbolToDelete) {
          delete state.stockIcons[symbolToDelete];
        }
        state.loading = false;
      })
      .addCase(deleteStockSvgThunk.rejected, (state, { payload }) => {
        state.loading = false;
        state.error = payload || 'Failed to delete stock SVG';
      });
  },
});

export const { resetError, clearStockIcons } = stockIconsSlice.actions;

export default stockIconsSlice.reducer;